import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { AuthSelectors } from '@core/auth/store';
import { isFatalError } from '@core/errors/store/selectors';
import { ScreenSize } from '@core/layout/models/screen-size.enum';
import { BusyIndicatorService } from '@core/layout/services/busy-indicator.service';
import { LayoutActions } from '@core/layout/store';
import { CloseRightPanel, OpenRightPanel, ToggleLeftPanel } from '@core/layout/store/actions';
import { getIsCompactMode, getScreenSize } from '@core/layout/store/selectors';
import { MenuActions } from '@core/menu/store';
import { DisableMainMenuPopup, EnableMainMenuPopup } from '@core/menu/store/actions';
import { ShowLongBreadcrumb, ShowShortBreadcrumb } from '@core/navigation/store/actions';
import { UserActions, UserSelectors } from '@core/user/store';
import { isNotAuthorized, isUserLoggedIn } from '@core/user/store/selectors';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { FatalErrorsComponent } from '../core/errors/fatal-errors/fatal-errors.component';
import { BusyIndicatorComponent } from '../core/layout/components/busy-indicator/busy-indicator.component';
import { ShellComponent } from '../core/layout/components/shell/shell.component';
import { TopbarComponent } from '../core/layout/components/topbar/topbar.component';
import { MainMenuComponent } from '../core/menu/main-menu/main-menu.component';
import { UserMenuComponent } from '../core/menu/user-menu/user-menu.component';
import { BreadcrumbComponent } from '../core/navigation/breadcrumb/breadcrumb.component';
import { NotAuthorizedComponent } from '../core/user/not-authorized/not-authorized.component';
import { UserPanelComponent } from '../core/user/user-panel/user-panel.component';
import { UserTopbarSectionComponent } from '../core/user/user-topbar-section/user-topbar-section.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NotAuthorizedComponent,
    NgTemplateOutlet,
    ShellComponent,
    TopbarComponent,
    BreadcrumbComponent,
    UserTopbarSectionComponent,
    MainMenuComponent,
    UserPanelComponent,
    UserMenuComponent,
    RouterOutlet,
    FatalErrorsComponent,
    BusyIndicatorComponent,
    AsyncPipe,
  ],
})
export class AppComponent implements OnDestroy {
  private _store = inject(Store);
  private _busyService = inject(BusyIndicatorService);

  private _destroy$: Subject<boolean> = new Subject<boolean>();
  isIframe = window !== window.parent && !window.opener;
  isUserLoggedIn$ = this._store.select(isUserLoggedIn);
  isNotAuthorized$ = this._store.select(isNotAuthorized);
  isFatalError$ = this._store.select(isFatalError);
  isCompactMode$ = this._store.select(getIsCompactMode);
  isAuthenticated$ = this._store
    .select(AuthSelectors.isAuthenticated) // TODO: może dwa serwisy ? jeden wewnętrzny a drugi zewnętrzny?
    .pipe(
      takeUntil(this._destroy$),
      tap((isAuthenticated) => {
        if (isAuthenticated) {
          this._store.dispatch(new UserActions.LoadUserData());
          this._store.dispatch(new UserActions.LoadUILanguages());
        }
      }),
    )
    .subscribe();

  currentUserId$ = this._store
    .select(UserSelectors.getCurrentUserId)
    .pipe(
      takeUntil(this._destroy$),
      tap((currentUserId) => {
        if (currentUserId) {
          this._store.dispatch(new MenuActions.LoadMainMenu());
          this._store.dispatch(new LayoutActions.InitLayout());
        }
      }),
    )
    .subscribe();

  screenSize$ = this._store
    .select(getScreenSize)
    .pipe(
      takeUntil(this._destroy$),
      filter((size) => !!size),
      tap((size) => {
        if (size <= ScreenSize.MD) {
          this._store.dispatch(new ShowShortBreadcrumb());
          this._store.dispatch(new DisableMainMenuPopup());
        } else {
          this._store.dispatch(new ShowLongBreadcrumb());
          this._store.dispatch(new EnableMainMenuPopup());
        }
      }),
    )
    .subscribe();

  constructor() {
    this.displayBusyIndicatorOnColdStart();
  }

  ngOnDestroy(): void {
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  onMenuBarClick() {
    this._store.dispatch(new ToggleLeftPanel());
  }

  closeUserSidenav() {
    this._store.dispatch(new CloseRightPanel());
  }

  openRightPanel() {
    this._store.dispatch(new OpenRightPanel());
  }

  private displayBusyIndicatorOnColdStart() {
    this._busyService.newOperation();
    setTimeout(() => this._busyService.operationFinished(), 1000);
  }
}
